import React, { useState, useEffect } from 'react';
import { TrendingDown, Plus, X, Calendar, Filter, Edit2, Trash2, DollarSign } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const getBrazilDate = () => {
  const now = new Date();
  const brazilTime = new Date(now.getTime() - (3 * 60 * 60 * 1000));
  return brazilTime.toISOString().split('T')[0];
};

const WithdrawalsManagement = () => {
  const { user } = useAuth();
  const [withdrawals, setWithdrawals] = useState([]);
  const [bettingHouses, setBettingHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningData, setWarningData] = useState(null);

  // Filtros
  const [filters, setFilters] = useState({
    houseId: '',
    startDate: '',
    endDate: '',
    withdrawalType: ''
  });

  // Formulário
  const [formData, setFormData] = useState({
    betting_house_id: '',
    withdrawal_type: 'partial',
    amount: '',
    source_type: 'profit',
    date: getBrazilDate(),
    comments: ''
  });

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Carregar casas de aposta
      const housesRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/betting-houses?user_id=${user.id}`
      );
      if (housesRes.ok) {
        const housesData = await housesRes.json();
        setBettingHouses(housesData);
      }

      // Carregar saques
      await loadWithdrawals();
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWithdrawals = async () => {
    try {
      const params = new URLSearchParams({ user_id: user.id });
      
      if (filters.houseId) params.append('betting_house_id', filters.houseId);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.withdrawalType) params.append('withdrawal_type', filters.withdrawalType);

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/withdrawals?${params}`
      );
      
      if (res.ok) {
        const data = await res.json();
        setWithdrawals(data);
      }
    } catch (error) {
      console.error('Erro ao carregar saques:', error);
    }
  };

  useEffect(() => {
    if (user) {
      loadWithdrawals();
    }
  }, [filters, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Verificar se o saldo global é negativo
    const currentBalance = getCurrentBalance(formData.betting_house_id);
    const withdrawAmount = parseFloat(formData.amount) || 0;
    
    if (currentBalance < 0 && !editingId) {
      // Mostrar modal de aviso
      const newBalance = currentBalance - withdrawAmount;
      setWarningData({
        currentBalance,
        withdrawAmount,
        newBalance,
        houseName: getHouseName(formData.betting_house_id)
      });
      setShowWarningModal(true);
      return;
    }

    // Se não houver aviso ou for edição, prosseguir normalmente
    await processWithdrawal();
  };

  const processWithdrawal = async () => {
    try {
      const url = editingId
        ? `${process.env.REACT_APP_BACKEND_URL}/api/withdrawals/${editingId}?user_id=${user.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/withdrawals?user_id=${user.id}`;

      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        alert(editingId ? '✅ Saque atualizado!' : '✅ Saque registrado!');
        resetForm();
        loadWithdrawals();
        loadData(); // Recarregar para atualizar saldos
        setShowWarningModal(false);
        setWarningData(null);
      } else {
        const error = await res.json();
        alert(`❌ Erro: ${error.detail}`);
      }
    } catch (error) {
      console.error('Erro ao salvar saque:', error);
      alert('❌ Erro ao salvar saque');
    }
  };

  const handleDelete = async (id) => {
    console.log('🗑️ handleDelete chamado:', { id, userId: user?.id });
    
    if (!user || !user.id) {
      alert('❌ Erro: Usuário não autenticado');
      console.error('User não disponível:', user);
      return;
    }
    
    if (!window.confirm('Deletar este saque? O valor será restaurado ao saldo da banca.')) {
      console.log('❌ Usuário cancelou a exclusão');
      return;
    }

    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/withdrawals/${id}?user_id=${user.id}`;
      console.log('🔄 Fazendo DELETE para:', url);
      
      const res = await fetch(url, { method: 'DELETE' });
      
      console.log('📡 Resposta recebida:', { status: res.status, ok: res.ok });

      if (res.ok) {
        const data = await res.json();
        console.log('✅ Saque deletado com sucesso:', data);
        alert('✅ Saque deletado e valor restaurado!');
        loadWithdrawals();
        loadData();
      } else {
        const error = await res.json();
        console.error('❌ Erro na resposta:', error);
        alert(`❌ Erro ao deletar: ${error.detail || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('❌ Erro ao deletar saque:', error);
      alert(`❌ Erro ao deletar saque: ${error.message}`);
    }
  };

  const startEdit = (withdrawal) => {
    setEditingId(withdrawal.id);
    setFormData({
      betting_house_id: withdrawal.betting_house_id,
      withdrawal_type: withdrawal.withdrawal_type,
      amount: withdrawal.amount,
      source_type: withdrawal.source_type,
      date: withdrawal.date,
      comments: withdrawal.comments || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      betting_house_id: '',
      withdrawal_type: 'partial',
      amount: '',
      source_type: 'profit',
      date: getBrazilDate(),
      comments: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getHouseName = (houseId) => {
    const house = bettingHouses.find(h => h.id === houseId);
    return house ? house.name : 'Casa não encontrada';
  };

  const getCurrentBalance = (houseId) => {
    const house = bettingHouses.find(h => h.id === houseId);
    return house ? house.current_bankroll || 0 : 0;
  };

  const totalWithdrawn = withdrawals.reduce((sum, w) => sum + (w.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-0">
      {/* Modal de Aviso - Saldo Negativo */}
      {showWarningModal && warningData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border-2 border-amber-500 max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                ⚠️ Saldo Global Negativo
              </h3>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-900/50 rounded-lg p-4">
                <p className="text-sm text-gray-300 mb-3">
                  A casa <span className="font-bold text-white">{warningData.houseName}</span> tem saldo negativo no histórico completo:
                </p>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Saldo Global Atual:</span>
                    <span className="font-bold text-red-400">
                      R$ {warningData.currentBalance.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Valor do Saque:</span>
                    <span className="font-bold text-white">
                      - R$ {warningData.withdrawAmount.toFixed(2)}
                    </span>
                  </div>
                  <div className="border-t border-gray-700 pt-2 mt-2 flex justify-between">
                    <span className="text-gray-400">Novo Saldo Global:</span>
                    <span className="font-bold text-red-500">
                      R$ {warningData.newBalance.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-xs text-amber-300">
                  <strong>Atenção:</strong> Este saque aumentará sua dívida histórica total. Mesmo assim, você pode prosseguir se tiver lucro no período atual.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowWarningModal(false);
                  setWarningData(null);
                }}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={processWithdrawal}
                className="flex-1 px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors"
              >
                Continuar Mesmo Assim
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white flex items-center gap-2">
            <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-500" />
            Gerenciar Saques
          </h2>
          <p className="text-xs sm:text-sm text-gray-400 mt-1">
            Total sacado: <span className="text-cyan-400 font-bold">R$ {totalWithdrawn.toFixed(2)}</span>
          </p>
        </div>

        <button
          onClick={() => setShowForm(!showForm)}
          className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors flex items-center gap-2 justify-center font-medium text-sm sm:text-base"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? 'Cancelar' : 'Novo Saque'}
        </button>
      </div>

      {/* Formulário - Mobile Optimized */}
      {showForm && (
        <div className="bg-gray-800 rounded-lg p-3 sm:p-4 md:p-6 border border-gray-700">
          <h3 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4">
            {editingId ? 'Editar Saque' : 'Registrar Novo Saque'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            {/* Casa de Aposta */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">
                Casa de Aposta *
              </label>
              <select
                value={formData.betting_house_id}
                onChange={(e) => setFormData({ ...formData, betting_house_id: e.target.value })}
                required
                className="w-full px-3 py-2.5 sm:py-2 bg-gray-700 text-white text-sm rounded-lg focus:ring-2 focus:ring-cyan-500"
              >
                <option value="">Selecione...</option>
                {bettingHouses.map(house => (
                  <option key={house.id} value={house.id}>
                    {house.name} - Saldo: R$ {(house.current_bankroll || 0).toFixed(2)}
                  </option>
                ))}
              </select>
            </div>

            {/* Tipo de Saque */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">
                Tipo de Saque *
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, withdrawal_type: 'partial', amount: '' })}
                  className={`px-3 sm:px-4 py-2.5 sm:py-2 text-sm rounded-lg font-medium transition-colors ${
                    formData.withdrawal_type === 'partial'
                      ? 'bg-cyan-500 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Parcial
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const currentBalance = getCurrentBalance(formData.betting_house_id);
                    setFormData({
                      ...formData,
                      withdrawal_type: 'total',
                      amount: currentBalance
                    });
                  }}
                  disabled={!formData.betting_house_id}
                  className={`px-3 sm:px-4 py-2.5 sm:py-2 text-sm rounded-lg font-medium transition-colors ${
                    formData.withdrawal_type === 'total'
                      ? 'bg-cyan-500 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Total
                </button>
              </div>
            </div>

            {/* Valor e Origem (apenas se parcial) */}
            {formData.withdrawal_type === 'partial' && (
              <>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">
                    Valor do Saque (R$) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                    className="w-full px-3 py-2.5 sm:py-2 bg-gray-700 text-white text-sm rounded-lg focus:ring-2 focus:ring-cyan-500"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">
                    Origem do Saque *
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, source_type: 'profit' })}
                      className={`px-3 sm:px-4 py-2.5 sm:py-2 text-sm rounded-lg font-medium transition-colors ${
                        formData.source_type === 'profit'
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      💰 Lucro
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, source_type: 'capital' })}
                      className={`px-3 sm:px-4 py-2.5 sm:py-2 text-sm rounded-lg font-medium transition-colors ${
                        formData.source_type === 'capital'
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      🏦 Capital
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Valor Total (se total) */}
            {formData.withdrawal_type === 'total' && (
              <div className="p-3 sm:p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <p className="text-xs sm:text-sm text-cyan-400 mb-1">Saque Total</p>
                <p className="text-xl sm:text-2xl font-bold text-white">
                  R$ {(formData.amount || 0).toFixed(2)}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Todo o saldo será sacado desta casa
                </p>
              </div>
            )}

            {/* Data */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">
                Data do Saque *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="w-full px-3 py-2.5 sm:py-2 bg-gray-700 text-white text-sm rounded-lg focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            {/* Observações */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1.5 sm:mb-2">
                Observações
              </label>
              <textarea
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                rows={2}
                className="w-full px-3 py-2.5 sm:py-2 bg-gray-700 text-white text-sm rounded-lg focus:ring-2 focus:ring-cyan-500 resize-none"
                placeholder="Ex: Conta limitada, saque emergencial..."
              />
            </div>

            {/* Botões */}
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <button
                type="submit"
                className="flex-1 px-4 py-2.5 sm:py-2 bg-cyan-500 hover:bg-cyan-600 text-white text-sm rounded-lg font-medium transition-colors"
              >
                {editingId ? 'Atualizar' : 'Registrar'} Saque
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2.5 sm:py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filtros - Mobile Optimized */}
      <div className="bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-700">
        <div className="flex items-center gap-2 mb-2 sm:mb-3">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-xs sm:text-sm font-medium text-gray-300">Filtros</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
          <select
            value={filters.houseId}
            onChange={(e) => setFilters({ ...filters, houseId: e.target.value })}
            className="px-3 py-2.5 sm:py-2 bg-gray-700 text-white text-sm rounded-lg focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">Todas as casas</option>
            {bettingHouses.map(house => (
              <option key={house.id} value={house.id}>{house.name}</option>
            ))}
          </select>

          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            className="px-3 py-2.5 sm:py-2 bg-gray-700 text-white text-sm rounded-lg focus:ring-2 focus:ring-cyan-500"
            placeholder="Data inicial"
          />

          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            className="px-3 py-2.5 sm:py-2 bg-gray-700 text-white text-sm rounded-lg focus:ring-2 focus:ring-cyan-500"
            placeholder="Data final"
          />

          <select
            value={filters.withdrawalType}
            onChange={(e) => setFilters({ ...filters, withdrawalType: e.target.value })}
            className="px-3 py-2.5 sm:py-2 bg-gray-700 text-white text-sm rounded-lg focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">Todos os tipos</option>
            <option value="partial">Parcial</option>
            <option value="total">Total</option>
          </select>
        </div>

        {(filters.houseId || filters.startDate || filters.endDate || filters.withdrawalType) && (
          <button
            onClick={() => setFilters({ houseId: '', startDate: '', endDate: '', withdrawalType: '' })}
            className="mt-2 sm:mt-3 text-xs sm:text-sm text-cyan-400 hover:text-cyan-300"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {/* Lista de Saques - Mobile Optimized */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        {withdrawals.length === 0 ? (
          <div className="p-6 sm:p-8 text-center">
            <TrendingDown className="w-10 h-10 sm:w-12 sm:h-12 text-gray-600 mx-auto mb-2 sm:mb-3" />
            <p className="text-sm sm:text-base text-gray-400">Nenhum saque registrado</p>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">Clique em "Novo Saque" para começar</p>
          </div>
        ) : (
          <>
            {/* Desktop: Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Data</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Casa</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Tipo</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400">Valor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Origem</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-400">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {withdrawals.map((withdrawal) => (
                    <tr key={withdrawal.id} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">
                        {new Date(withdrawal.date).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-4 py-3 text-sm text-white">
                        {getHouseName(withdrawal.betting_house_id)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          withdrawal.withdrawal_type === 'total'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-cyan-500/20 text-cyan-400'
                        }`}>
                          {withdrawal.withdrawal_type === 'total' ? 'Total' : 'Parcial'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-red-400 text-right whitespace-nowrap">
                        - R$ {withdrawal.amount.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {withdrawal.source_type === 'profit' && '💰 Lucro'}
                        {withdrawal.source_type === 'capital' && '🏦 Capital'}
                        {withdrawal.source_type === 'mixed' && '🔄 Misto'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => startEdit(withdrawal)}
                            className="p-1.5 text-cyan-400 hover:bg-cyan-500/20 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(withdrawal.id)}
                            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                            title="Deletar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: Cards */}
            <div className="md:hidden p-2 space-y-2">
              {withdrawals.map((withdrawal) => (
                <div key={withdrawal.id} className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                  {/* Header: Data + Tipo + Ações */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">
                        {new Date(withdrawal.date).toLocaleDateString('pt-BR')}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        withdrawal.withdrawal_type === 'total'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-cyan-500/20 text-cyan-400'
                      }`}>
                        {withdrawal.withdrawal_type === 'total' ? 'Total' : 'Parcial'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => startEdit(withdrawal)}
                        className="p-1.5 text-cyan-400 hover:bg-cyan-500/20 rounded transition-colors"
                        title="Editar"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(withdrawal.id)}
                        className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                        title="Deletar"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Casa */}
                  <div className="mb-2">
                    <span className="text-sm font-medium text-white">
                      {getHouseName(withdrawal.betting_house_id)}
                    </span>
                  </div>

                  {/* Valor + Origem */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      {withdrawal.source_type === 'profit' && '💰 Lucro'}
                      {withdrawal.source_type === 'capital' && '🏦 Capital'}
                      {withdrawal.source_type === 'mixed' && '🔄 Misto'}
                    </div>
                    <span className="text-base font-bold text-red-400">
                      - R$ {withdrawal.amount.toFixed(2)}
                    </span>
                  </div>

                  {/* Comentários (se houver) */}
                  {withdrawal.comments && (
                    <div className="mt-2 pt-2 border-t border-gray-600">
                      <p className="text-xs text-gray-400">{withdrawal.comments}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WithdrawalsManagement;
